/**
 * Candidate processing types
 * These types represent the candidate generation and processing results
 */

import type { DateString, ProcessingPhase } from './core-types';
import type { MillageInputsForm } from './form-types';
import type { BondSeries, DRFInputs } from './inputs';
import type {
  DateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
  ExistingDebtProject,
  TaxableValueMap,
} from './project';

/**
 * Target constraint types for bond series
 */
export enum TargetType {
  DOES_NOT_EXCEED = 'does-not-exceed', // Maximum principal constraint
  ACCEPTS_OVERFLOW = 'accepts-overflow', // Can exceed target amount
}

/**
 * Target information for bond series principal constraints
 */
export interface TargetInformation {
  principalTargetAmount: number;
  targetType: TargetType;
}

/**
 * Base project elements - processed foundation data
 */
export interface BaseProjectElements {
  dateRecords: DateRecord[];
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate;
  dateRecordsMapByTaxYear: DateRecordsMapByTaxYear;
  taxableValueMap: TaxableValueMap;
  existingDebtInformation: {
    existingDebtProjects: ExistingDebtProject[];
    combinedDebtService: ExistingDebtProject;
    totalExistingDebt: number;
  };
}

/**
 * Millage shape adjustment for fine-tuning
 */
export interface MillageShapeAdjustment {
  taxYear: number;
  adjustmentType: 'add' | 'subtract' | 'multiply' | 'divide';
  adjustmentValue: number;
}

/**
 * Millage interval within a shape
 */
export interface MillageInterval {
  startYear: number;
  endYear: number;
  startMillage: number;
  endMillage: number;
  intervalType: 'flat' | 'linear' | 'exponential';
}

/**
 * Millage shape configuration
 */
export interface MillageShape {
  shapeId: string;
  shapeName: string;
  shapeType: 'linear' | 'log-accel' | 'log-decel';
  intervals: MillageInterval[];
  adjustments: MillageShapeAdjustment[];
}

/**
 * Complete millage information for a candidate
 */
export interface MillageInformation {
  millageShape: MillageShape;
  millageMap: Record<number, number>; // tax year -> millage rate
  peakMillage: number;
  averageMillage: number;
  millageUtilization: number;
  totalRevenue: number; // Total revenue calculated from millage rates
}

/**
 * Revenue map entry for a specific tax year
 */
export interface RevenueMapEntry {
  taxYear: number;
  growthRate: number;
  taxableValue: number;
  millage: number;
  revenue: number;
  existingDebt: number;
  revenueMinusExistingDebt: number;
  bondSeriesDetailsByBondId: { [key: string]: { isActive: boolean; accumulatedInterest: number } };
  totalAccumulatedInterest: number;
  availableRevenue: number;
  revenueCapacity: number;
  isTunable: boolean;
}

/**
 * Revenue map - tax year to revenue entry
 */
export interface RevenueMap {
  [taxYear: number]: RevenueMapEntry;
}

/**
 * Cash flow breakdown by bond series
 * Based on engine-prototype's BondPaymentRecord structure
 */
export interface BondPaymentCashFlowRecord {
  bondSeriesId: string;
  bondName: string; // Added: Individual bond name
  bondSeriesName: string;
  bondCFIssueDate: DateString;
  isActive: boolean;
  isInterestOnly: boolean;
  principal: number;
  interestRate: number; // From BondPaymentDateMap
  newInterest: number; // Interest generated by this principal payment
  futureInterestObligation: number; // Future interest commitments from this principal
  totalInterest: number; // newInterest + futureInterestObligation
  totalPayment: number; // principal + totalInterest
}

/**
 * Cash flow record for a specific payment date
 * Comprehensive structure matching engine-prototype for accurate calculations
 */
export interface CashFlow {
  // Date and fiscal year context
  paymentDate: DateString;
  relatedTaxBillYear: number; // Tax bill year (renamed from taxYear)
  relatedFiscalYearEndingDate: DateString; // Fiscal year ending date
  isFinalFiscalYearPayment: boolean; // Is this the last payment in fiscal year
  isActive: boolean; // Is this an active payment date

  // Millage and revenue context
  millageRate: number; // Millage rate for this tax year
  totalRevenueForTaxYear: number; // Total revenue for the tax year
  paymentRevenuePercentage: number; // Percentage of revenue for this payment
  paymentRevenuePortion: number; // Actual revenue portion for this payment

  // Revenue flow tracking
  drfStartingBalance: number; // DRF balance at start
  debtPaymentAmount: number; // Existing debt payment total (renamed from cumulativeExistingDebt)
  existingDebtPrincipal: number; // Existing debt principal portion
  existingDebtInterest: number; // Existing debt interest portion
  revenueMinusExistingDebt: number; // Revenue after existing debt
  isInterestOnly: boolean; // Interest-only payment period
  interestObligation: number; // Total interest obligation
  availableForBonds: number; // Available after interest

  // Bond payment details
  bondPayments: BondPaymentCashFlowRecord[]; // Detailed bond payments
  totalBondPayment: number; // Sum of all bond payments

  // DRF tracking
  drfAdjustment: number; // DRF change this period
  drfEndingBalance: number; // DRF balance at end
  drfNetChange: number; // Net DRF change
  drfNetRevenue: number; // Revenue after DRF adjustment
}

/**
 * Results for a specific bond series
 */
export interface BondSeriesResult {
  bondSeriesId: string;
  bondSeriesName: string;
  totalPrincipal: number;
  totalInterest: number;
  totalPayment: number;
  isViable: boolean;
  failureMessages: string[];
  // Target achievement tracking
  targetAchieved?: boolean;
  targetShortfall?: number;
  targetExcess?: number;
  targetComplianceMessage?: string;
}

/**
 * Complete candidate results
 */
export interface CandidateResult {
  isViable: boolean;
  failureMessages: string[];
  totalRevenue: number;
  totalInterest: number;
  totalPrincipal: number;
  revenueUtilization: number;
  interestRatio: number;
  efficiency: number; // Principal-to-total spending ratio (value vs interest)
  bondSeriesResults: BondSeriesResult[];
}

/**
 * Tax year based project plan record
 * Comprehensive annual summary of financial data by tax year
 */
export interface TaxYearBasedProjectPlanRecord {
  taxYear: number;
  taxableValue: number;
  millage: number;
  totalRevenue: number;
  existingDebt: number;
  netRevenue: number;
  bondPayments: number[];
  totalBondPayments: number;
  totalPayment: number;
  drfAdjustment: number;
  drfEndingBalance: number;
}

/**
 * Optimization history entry - tracks the candidate's journey from creation through optimization
 *
 * Entry types:
 * - 'initial-shape': Records the initial millage shape when candidate is created
 * - 'optimization-skipped': Records when an optimization was analyzed but not needed
 * - 'utilization', 'five-year-fix', 'millage-adjustment', 'target-compliance': Applied optimizations
 * - 'drf-recovery': Recovery from negative DRF by reducing principal
 */
export interface OptimizationHistoryEntry {
  type:
    | 'initial-shape'
    | 'optimization-skipped'
    | 'utilization'
    | 'five-year-fix'
    | 'millage-adjustment'
    | 'drf-recovery'
    | 'target-compliance';
  description: string;
  timestamp: DateString;
  details?: {
    // Initial shape details
    shapeId?: string;
    processType?: string;
    // Optimization skip details
    optimizationType?: string;
    reason?: string;
    // Applied optimization details
    principalAdded?: number;
    principalMoved?: number;
    fromDate?: DateString;
    toDate?: DateString;
    bondSeriesAffected?: string[];
    drfImpact?: number;
    [key: string]: unknown; // Allow for optimization-specific details
  };
}

/**
 * Complete candidate with all processing information
 *
 * NOTE: Project-level data (dateRecords, taxableValueMap, existingDebtProjects) and
 * configuration-level data (bondSeries, drfInputs) are no longer stored in candidates.
 * Access these via project and configuration references passed to components.
 */
export interface Candidate {
  id: string;
  name: string;
  description: string;
  isProcessed: boolean;
  createdInPhase: ProcessingPhase;
  // Removed: baseProjectElements (use project reference)
  // Removed: drfInformation (use configuration reference)
  // Removed: bondSeries (use configuration reference)
  millageInformation: MillageInformation;
  revenueMap: RevenueMap;
  cashFlows: CashFlow[];
  distributionStrategy: 'unlimited' | 'all-targeted' | 'mixed-targeting';
  results: CandidateResult;
  taxYearBasedProjectPlan?: TaxYearBasedProjectPlanRecord[];
  cashFlowAdjustments?: unknown[]; // Will be expanded later
  optimizationHistory?: OptimizationHistoryEntry[]; // Track all optimizations applied
}

/**
 * Candidate with project/configuration context
 * Use this when passing candidates to UI components that need project data
 */
export interface CandidateWithContext {
  candidate: Candidate;
  projectId: string;
  configurationId: string;
}

/**
 * Complete context for displaying candidate details
 * Components receive this to access both candidate results and project/config data
 */
export interface CandidateDisplayContext {
  // Candidate-specific results
  candidate: Candidate;

  // Project-level data (shared across all candidates)
  dateRecords: DateRecord[];
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate;
  dateRecordsMapByTaxYear: DateRecordsMapByTaxYear;
  taxableValueMap: TaxableValueMap;
  existingDebtProjects: ExistingDebtProject[];
  combinedDebtService: ExistingDebtProject;

  // Configuration-level data (shared within configuration)
  bondSeries: BondSeries[];
  drfInputs: DRFInputs;
  millageInputs: MillageInputsForm;
}
